import * as React from 'react';
import { useEffect, useState } from 'react';
import { getFloors, getUnits, Unit } from '../../../../api/Engrain';
import {
    getAllUnitColors,
    MapStopDto,
    UnitMapConfiguration,
    updateUnitMapConfiguration
} from '../../../../api/UnitMap';
import { useFetch } from '../../../../hooks/useFetch';
import CollapsibleCard from '../../../common/CollapsibleCard';
import Form from '../../../common/form/Form';
import FormValidationDisplay from '../../../common/form/ValidationDisplay';
import { LoadingDisplay } from '../../../common/LoadingOverlay';
import StopListEditor from './integrationControls/unitMapControls/StopListEditor';
import { Floor, MapContext } from '../../../common/UnitMap/UnitMapTypes';
import MapIdEnterInput from './integrationControls/unitMapControls/MapIdEnterInput';
import UnitColorEditor from './integrationControls/unitMapControls/UnitColorEditor';
import '../../../../styles/UnitMapConfiguration.css';
import StickyUnitMap from './integrationControls/StickyUnitMap';
import {userIsCompanyManageUser,userIsPropertyManageUser,userIsSystemManageUser} from '../../../../utilities/identity';

interface Props {
    errorMessage?: string;
    setError: (error?: string) => void;
    setIsLoading: (isLoading: boolean) => void;
    onChange: (newConfig?: UnitMapConfiguration) => void;
    propertyId: number;
    isLoading: boolean;
    unitMapConfiguration: UnitMapConfiguration;
}

const newStops: MapStopDto[] = [
    {
        stopNumber: 0,
        description: '',
        directions: '',
        x: 0,
        y: 0,
        floor: '',
        isAmenity: false
    },
    {
        stopNumber: 1,
        description: '',
        directions: '',
        x: 0,
        y: 0,
        floor: '',
        isAmenity: false
    }
];

const UnitMapConfigurationTab = (props: Props) => {
    let unitMapConfiguration = props.unitMapConfiguration;
    let isLoading = props.isLoading;

    const [mapIsValid, setMapValid] = useState(false);
    const [mapError, setMapError] = useState<string>('');
    const [mapDataLoading, setMapDataLoading] = useState(true);
    const [selectedFloor, selectFloor] = useState<string>();
    const [selectedStop, selectStop] = useState<number>();
    const [allFloors, setAllFloors] = useState<Floor[]>();
    const [locationEnabled, setLocationEnabled] = useState(false);
    const [map, setMap] = useState<any>();
    const [allUnits, setAllUnits] = useState<Unit[]>();
    const [stopToEdit, setStopToEdit] = useState<number | undefined>();
    
    const isUser = (
    userIsCompanyManageUser() ||
    userIsPropertyManageUser() ||
    userIsSystemManageUser()
    );

    const handleSubmit = async () => {
        if (isUser) return;
        props.setIsLoading(true);
        let result = await updateUnitMapConfiguration(props.propertyId, unitMapConfiguration);
        props.setError(result.error);
        props.setIsLoading(false);
    };

    const handleMapClick = (x: number, y: number) => {
        if (isUser) return;
        let coords = map.toLocal([x, y]);
        if (stopToEdit !== undefined) {
            let stops = unitMapConfiguration.stops;
            stops[stopToEdit].x = parseInt(coords[0], 10);
            stops[stopToEdit].y = parseInt(coords[1], 10);
            props.onChange({
                ...unitMapConfiguration,
                stops: stops
            });
            setStopToEdit(undefined);
        }
    };
    useEffect(() => {
        if (isLoading || !mapIsValid || !unitMapConfiguration.unitMapId) {
            setMapDataLoading(true);
            return;
        }
        let floorPromise = getFloors(unitMapConfiguration.unitMapId).then(floors => {
            setAllFloors(floors);
            selectFloor(floors[0].value);
        });
        let unitPromise = getUnits(unitMapConfiguration.unitMapId).then(units => {
            setAllUnits(units);
        });
        Promise.all([floorPromise, unitPromise]).then(() => {
            setMapDataLoading(false);
        });
    }, [isLoading, unitMapConfiguration.unitMapId, mapIsValid]);
    const { data: unitMapColorData } = useFetch(() => getAllUnitColors(), [], undefined);
    return (
        <Form
            wasFormValidated={true}
            inBootstrapColumn={true}
            submitButtonText={'Save'}
            onSubmit={handleSubmit}
            onFormValidated={() => {
                return;
            }}
            disabled={isUser}
        >
            <CollapsibleCard
                cardTitle="Map Enabled on Property"
                cardOpen={!props.unitMapConfiguration.isDeleted}
                onCardOpenChange={(open: boolean) => {
                    if (isUser) return;
                    props.onChange(
                        open
                            ? { ...unitMapConfiguration, isDeleted: false }
                            : { ...unitMapConfiguration, isDeleted: true }
                    );
                }}
            >
                <>
                    <MapIdEnterInput
                        mapId={props.unitMapConfiguration.unitMapId}
                        setMapId={newId => {
                            if (isUser) return;
                            let stops = unitMapConfiguration.stops;
                            let units = unitMapConfiguration.units;
                            if (props.unitMapConfiguration.unitMapId !== newId) {
                                stops = newStops;
                                units = [];
                            }
                            props.onChange({
                                ...unitMapConfiguration,
                                unitMapId: newId,
                                stops,
                                units
                            });
                            setMapValid(true);
                        }}
                        isValid={mapIsValid}
                        setIsValid={setMapValid}
                        errorMessage={mapError}
                        setError={e => {
                            if (isUser) return;
                            setMapError(e ? e : '');
                        }}
                        disabled={isUser}
                    />

                    {unitMapConfiguration &&
                    mapIsValid &&
                    !mapDataLoading &&
                    !mapError &&
                    unitMapColorData ? (
                        <MapContext.Provider
                            value={{
                                configuration: unitMapConfiguration,
                                setConfiguration: newConfig => {
                                    if (isUser) return;
                                    props.onChange(newConfig);
                                },
                                colors: unitMapColorData,
                                allFloors,
                                selectedFloor,
                                selectFloor,
                                setAllFloors,
                                map,
                                setMap,
                                selectStop,
                                locationEnabled,
                                setLocationEnabled,
                                selectedStopIndex: selectedStop,
                                onMapClick: handleMapClick
                            }}
                        >
                            <FormValidationDisplay errorMessage={mapError} />
                            <StickyUnitMap />
                            <StopListEditor
                                onStopEdit={index => {
                                    if (isUser) return;
                                    setStopToEdit(index);
                                }}
                            />
                            {allUnits !== undefined ? (
                                <UnitColorEditor units={allUnits} />
                            ) : (
                                <LoadingDisplay />
                            )}
                        </MapContext.Provider>
                    ) : mapDataLoading ? (
                        <LoadingDisplay />
                    ) : (
                        <FormValidationDisplay errorMessage={mapError} />
                    )}
                </>
            </CollapsibleCard>
            <FormValidationDisplay errorMessage={props.errorMessage} />
        </Form>
    );
};

export default UnitMapConfigurationTab;



// WEBPACK FOOTER //
// ./src/components/pages/admin/editProperty/UnitMapConfigurationTab.tsx